import {Toast} from "antd-mobile";
import React, {FC, useState} from "react";
import {useParams} from "react-router";
import {Button} from "antd-mobile";
import {useNavigate} from "react-router-dom";
import {setInitPassword, verifyPayPass, editPassword} from "@api/user";
import NumberKeyboard from "@components/NumberKeyboard/index";
import './index.scss';

const StrPwd = /^\d{1,}$/;
const PWD_LENGTH = 6;

type PwdField = 'new' | 'repeat' | 'old';

const Password: FC = ()=> {
  const urlParam = useParams();
  const navigate = useNavigate();
  const {type, entry} = urlParam;
  const [newPwd, setNewPwd] = useState('');
  const [newRepeatPwd, setRepeatNewPwd] = useState('');
  const [hasError, setHasError] = useState(false);
  const [repeatHasError, setRepeatHasError] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(true);
  const [updateOldPwd, setUpdateOldPwd] = useState('');
  const [updateError, setUpdateError] = useState(false);
  const [focusField, setFocusField] = useState<PwdField>('new');

  // 修改流程的第一步只输入旧密码，键盘始终作用在旧密码上
  const isOldStep = type === 'update' && updateStatus;
  const activeField: PwdField = isOldStep ? 'old' : focusField;

  const setNewPassword = (e: string)=> {
    if (!StrPwd.test(e) && e.length === 6) {
      setHasError(true)
    } else {
      setHasError(false)
    }
    setNewPwd(e);
    // 第一栏改动后重算两栏是否一致，避免红字提示停留在旧结果
    setRepeatHasError(newRepeatPwd !== e);
  }

  const setRepeatPassword = (e: string)=> {
    setRepeatNewPwd(e);
    if (newPwd === e) {
      setRepeatHasError(false);
    } else {
      setRepeatHasError(true);
    }
  }

  const setUpdatePwd = (val: string)=> {
    if (!StrPwd.test(val) && val.length === 6) {
      setUpdateError(true)
    } else {
      setUpdateError(false)
    }
    setUpdateOldPwd(val)
  }

  const makePassword = async ()=> {
    try {
      if(newPwd === newRepeatPwd && newPwd.length === 6) {
        // 设置密码
        const res = await setInitPassword(newPwd);
        if (res.code === 200) {
          Toast.success('设置成功');
          setTimeout(()=> {
            if (entry === 'pay') {
              navigate('/cardpay');
            }
          }, 1000)
        }
      } else {
        Toast.info('输入密码不一致');
      }
    } catch (e) {
      console.log(e)
    }
  }

  // 切换显示组件
  const changePwdView = async ()=> {
    try {
      if (updateOldPwd.length === 6 && StrPwd.test(updateOldPwd)) {
        // 修改密码
        if (updateStatus) {
          const res = await verifyPayPass(updateOldPwd);
          if (res.code === 200) {
            Toast.info('验证成功!')
            setUpdateStatus(false)
          }
        } else {
          if (newPwd !== newRepeatPwd || newPwd.length !== 6) {
            return Toast.info('输入密码不一致');
          }
          const res_info = await editPassword(newRepeatPwd);
          if (res_info.code === 200) {
            Toast.success('设置成功');
            if (entry === 'pay') {
              navigate('/cardpay');
            }
          }
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const inputNumber = (num: string)=> {
    if (activeField === 'old') {
      if (updateOldPwd.length >= PWD_LENGTH) return;
      return setUpdatePwd(updateOldPwd + num);
    }
    if (activeField === 'new') {
      if (newPwd.length >= PWD_LENGTH) return;
      const next = newPwd + num;
      setNewPassword(next);
      // 首个密码输完自动跳到重复输入
      if (next.length === PWD_LENGTH) setFocusField('repeat');
      return;
    }
    if (newRepeatPwd.length >= PWD_LENGTH) return;
    setRepeatPassword(newRepeatPwd + num);
  }

  const deleteNumber = ()=> {
    if (activeField === 'old') return setUpdatePwd(updateOldPwd.slice(0, -1));
    if (activeField === 'new') return setNewPassword(newPwd.slice(0, -1));
    if (!newRepeatPwd) return setFocusField('new');
    setRepeatPassword(newRepeatPwd.slice(0, -1));
  }

  const clearNumber = ()=> {
    if (activeField === 'old') return setUpdatePwd('');
    if (activeField === 'new') return setNewPassword('');
    setRepeatPassword('');
  }

  const pwdCells = (value: string, field: PwdField)=> {
    const cells = [];
    for (let i = 0; i < PWD_LENGTH; i++) {
      cells.push(
        <div className={'pwd-cell'} key={i}>
          {value.length > i && <span className={'pwd-dot'}/>}
          {activeField === field && value.length === i && <span className={'pwd-cursor'}/>}
        </div>
      )
    }
    return (
      <div
        className={activeField === field ? 'pwd-cells is-active' : 'pwd-cells'}
        onClick={()=> field !== 'old' && setFocusField(field)}
      >
        {cells}
      </div>
    )
  }

  const pwdField = (label: string, value: string, field: PwdField, hint: string)=> {
    return (
      <div className={'pwd-field'}>
        <div className={'pwd-label'}>{label}</div>
        {pwdCells(value, field)}
        <div className={'pwd-hint'}>{hint}</div>
      </div>
    )
  }

  const pageHeader = (title: string, tip: string)=> {
    return (
      <div className={'pwd-header'}>
        <div className={'pwd-title'}>{title}</div>
        <div className={'pwd-tip'}>{tip}</div>
      </div>
    )
  }

  const submitBtn = (onPress: ()=> void, ready: boolean)=> {
    return (
      <div className={'pwd-submit'}>
        <Button type="primary" className={ready ? '' : 'is-idle'} onClick={()=> onPress()}>确定</Button>
      </div>
    )
  }

  const pageView = (content: React.ReactNode)=> {
    return (
      <div className={'pwd-page'}>
        <div className={'pwd-body'}>{content}</div>
        <NumberKeyboard
          onInput={(num)=> inputNumber(num)}
          onDelete={()=> deleteNumber()}
          onClear={()=> clearNumber()}
        />
      </div>
    )
  }

  const passwordCon = ()=> {
    return (
      <>
        {pwdField('请输入密码', newPwd, 'new', hasError ? '请输入六位数字密码' : '')}
        {pwdField(
          '请重复输入密码',
          newRepeatPwd,
          'repeat',
          repeatHasError && newRepeatPwd.length === PWD_LENGTH ? '两次输入的密码不一致' : ''
        )}
      </>
    )
  }

  const setPassword = ()=> {
    const ready = newPwd.length === PWD_LENGTH && newRepeatPwd.length === PWD_LENGTH;
    return pageView(
      <>
        {pageHeader('设置支付密码', '请设置六位数字支付密码，用于余额支付时验证身份')}
        {passwordCon()}
        {submitBtn(makePassword, ready)}
      </>
    )
  }

  const updatePassword = ()=> {
    if (updateStatus) {
      return pageView(
        <>
          {pageHeader('修改支付密码', '请先验证原六位数字支付密码')}
          {pwdField('旧密码', updateOldPwd, 'old', updateError ? '请输入六位数字密码' : '')}
          {submitBtn(changePwdView, updateOldPwd.length === PWD_LENGTH)}
        </>
      )
    }
    return pageView(
      <>
        {pageHeader('修改支付密码', '请设置新的六位数字支付密码')}
        {passwordCon()}
        {submitBtn(changePwdView, newPwd.length === PWD_LENGTH && newRepeatPwd.length === PWD_LENGTH)}
      </>
    )
  }

  if (type === 'set') return setPassword();
  if (type === 'update') return updatePassword();
  return setPassword();

}

export default Password;
